<template>
  <FullScreenOverlay :initializing="fetchingItem || transitioning">
    <div class="fetching-container" v-if="fetchingItem || transitioning">
      <p class="lead text-muted no-margin-bottom">
        <i class="fa fa-circle-o-notch fa-spin"/> Preparing...
      </p>
    </div>

    <PrintLayoutContainer v-if="!fetchingItem && !transitioning">
      <div
        class="container top-bar"
        slot="top"
        v-if="!fetchingItem">
        <div class="print-options">
          <b>Include:</b>
          <form @submit.prevent>
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="printOptions.includeObjective"> Objective
              </label>
            </div>
            <div class="checkbox" v-if="hasPassages">
              <label>
                <input type="checkbox" v-model="printOptions.includePassages"> Passages
              </label>
            </div>
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="printOptions.includeItem"> Item Prompt
              </label>
            </div>
            <div class="checkbox">
              <label>
                <input type="checkbox" v-model="printOptions.includeResponse"> Model Student Response
              </label>
            </div>
          </form>
        </div>
        <div class="print-quota" v-if="isTrial">
          <p :class="{'text-danger': printQuotaExceeded, 'text-muted': !printQuotaExceeded}">
            {{ countRemaining }} of {{ printQuota }} prints remaining this month
          </p>
        </div>
        <div class="print-actions">
          <p><button
            class="btn btn-block"
            :class="levelButtonClass"
            @click="printAndClose"><i class="fa fa-print"/> Print</button></p>
          <p><button class="btn btn-block btn-default" @click="closePreview"><i class="fa fa-times"/> Cancel</button></p>
        </div>
      </div>

      <div slot="body">
        <PrintPage v-if="hasPassages && printOptions.includePassages">
          <h1 class="print-heading" v-if="showHeadingOrObjectiveWithPassages">
            <span>{{ objectiveTitle }} <template v-if="!teksVisibility">({{ item.standard_ref | shortStandardRefFromLongRef('CCSS') }})</template></span>
          </h1>
          <section v-if="printOptions.includeObjective && objectiveText && showHeadingOrObjectiveWithPassages">
            <h2 class="dividing-header"><span class="bounded">Objective</span></h2>
            <WikiText :text="objectiveText"/>
          </section>
          <h2 class="dividing-header"><span class="bounded">Passages</span></h2>
          <section v-for="passage in item.passages" :key="passage.uuid">
            <h3>{{ passage.passage_title }}</h3>
            <div class="passage">
              <MarkdownText :text="passage.passage_text"/>
            </div>
          </section>
        </PrintPage>

        <PrintPage v-if="printOptions.includeItem || printOptions.includeResponse || !showHeadingOrObjectiveWithPassages">
          <h1 class="print-heading" v-if="!showHeadingOrObjectiveWithPassages">
            <span>{{ objectiveTitle }} <template v-if="!teksVisibility">({{ item.standard_ref | shortStandardRefFromLongRef('CCSS') }})</template></span>
          </h1>
          <section v-if="printOptions.includeObjective && objectiveText && !showHeadingOrObjectiveWithPassages">
            <h2 class="dividing-header"><span class="bounded">Objective</span></h2>
            <WikiText :text="objectiveText"/>
          </section>

          <section>
            <div v-if="printOptions.includeItem">
              <h2 class="dividing-header">
                <span class="bounded">Item Prompt</span>
              </h2>

              <div
                v-for="(p, index) in item.parts"
                :key="index">
                <h4 v-if="item.parts.length > 1">Part {{ index + 1 }}</h4>
                <WikiText :text="p.prompt" />
              </div>
            </div>

            <div v-if="printOptions.includeResponse">
              <h2 class="dividing-header"><span class="bounded">Model Student Response</span></h2>
              <div
                v-for="(p, index) in item.parts"
                :key="index">
                <h4 v-if="item.parts.length > 1">Part {{ index + 1 }}</h4>
                <WikiText :text="p.response" />
              </div>
            </div>
          </section>
        </PrintPage>
      </div>
    </PrintLayoutContainer>
  </FullScreenOverlay>
</template>

<script>
import FullScreenOverlay from '@/components/FullScreenOverlay'
import PrintLayoutContainer from '@/components/PrintLayoutContainer'
import PrintPage from '@/components/PrintPage'
import WikiText from '@/components/WikiText'
import MarkdownText from '@/components/MarkdownText'
import itemMixin from '@/components/goals/common/item_mixin'
import { isValidObjectiveLevel, levelButtonClass } from '@/components/goals/common/objectives'

import { mapGetters } from 'vuex'

export default {
  name: 'PrintableObjective',
  components: {
    FullScreenOverlay,
    PrintLayoutContainer,
    PrintPage,
    WikiText,
    MarkdownText
  },
  mixins: [itemMixin],
  props: {
    itemId: {
      type: String,
      required: true
    },
    objectiveLevel: {
      type: String,
      required: true,
      validator: isValidObjectiveLevel
    },
    objectiveTitle: {
      type: String,
      default: ''
    },
    objectiveText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      transitioning: true,
      fetchingItem: false,
      printOptions: {
        includeObjective: true,
        includeItem: true,
        includePassages: true,
        includeResponse: true
      }
    }
  },
  computed: {
    ...mapGetters('contentAccess', ['printQuota', 'printCount', 'printQuotaExceeded']),
    ...mapGetters('user', ['isTrial']),
    ...mapGetters('visibility', ['teksVisibility']),
    countRemaining() {
      return Math.max(0, this.printQuota - this.printCount)
    },
    levelButtonClass() {
      return levelButtonClass(this.objectiveLevel)
    },
    hasPassages() {
      return this.item && this.item.passages.length > 0
    },
    item() {
      return this.itemById(this.itemId)
    },
    showHeadingOrObjectiveWithPassages() {
      return this.hasPassages && this.printOptions.includePassages
    }
  },
  methods: {
    closePreview() {
      this.$emit('update:show', false)
    },
    printAndClose() {
      window.print()
      this.$emit('print', Object.assign({ has_passages: this.hasPassages }, this.printOptions))
      this.closePreview()
    },
    loadItem() {
      this.fetchingItem = true
      this.fetchItem(this.itemId).then(() => {
        this.fetchingItem = false
        if (!this.hasPassages) {
          this.printOptions.includePassages = false
        }
      })
    }
  },
  created() {
    this.loadItem()
    this.transitioning = true

    // set a minimum "wait time" so the user perceives the transition of the printable overlay animated
    // without this, the overlay could appear very quickly and user might think they navigated to a new page
    setTimeout(() => {
      this.transitioning = false
    }, 500)
  }
}
</script>

<style scoped lang="less">
.fetching-container {
  text-align: center;
  padding: 32px;
}
.top-bar {
  display: flex;
  max-width: 100%;
  .print-options {
    flex: 1 1 auto;

    .checkbox {
      margin-top: 2px;
      margin-bottom: 0px;
    }
  }
  .print-quota {
    flex: 2 1 auto;
    padding-top: 6px;
    text-align: right;
  }
  .print-actions {
    padding-left: 12px;
    flex: 0 1 auto;
  }
}
.print-heading {
  font-size: 18px;
  font-weight: normal;
  padding: 8px;
  border: 1px solid #ccc;
  color: #333;
  background: #efefef !important;
}
.dividing-header {
  font-size: 14px;
  font-weight: bold;
  line-height: 17px;
  border-top: 1px solid #333 !important;
  margin-top: 32px;

  .bounded {
    padding: 4px 8px;
    background: #444 !important;
    border: 1px solid #000;
    color: white !important;
    display: inline-block;
    position: relative;
    top: -1px;
  }
}
.passage {
  font-family: 'Times', 'serif';
}
</style>
